import { set } from 'lodash';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import {
  DaypartGlobalComponentItem,
  HttpMethod,
  TransactionPayload,
} from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import {
  ComponentTypes,
  DyProductSource,
  GlobalComponentsInterface,
  IComponent,
  IDaypart,
  ProjectConfigInterface,
  ScreenGlobalComponentInterface,
} from 'src/interfaces';

import { RowItem } from '../modules/CustomerTriggeredEventsTable.interface';
import { CustomerTriggeredEventsErrors } from './useCustomerTriggeredEventsPage.hook';

export const transformRowsToPayload = (
  rows: RowItem[],
  daypartGlobalComponents: DaypartGlobalComponentItem[],
  dayparts: IDaypart[],
  components: IComponent<any>[],
  globalComponents: GlobalComponentsInterface[],
  resourceName = 'daypartGlobalComponents',
) => {
  const daypartGlobalComponentMap = new Map<
    number,
    DaypartGlobalComponentItem
  >();
  daypartGlobalComponents.forEach((it) =>
    daypartGlobalComponentMap.set(Number(it.id), it),
  );
  // Track which api ids we have encountered in UI (so the rest can be deleted)
  const seenApiIds = new Set<number>();

  const ops: TransactionPayload[] = [];

  const daypartMap = new Map<number, IDaypart>();
  dayparts.forEach((daypart) => daypartMap.set(daypart.id, daypart));

  rows.forEach((row) => {
    const components = row.components || [];
    const eventName = row.name; // e.g., "Customer order display (COD)"

    components.forEach((component) => {
      const selected = component.selectedDayparts || [];
      selected.forEach((s) => {
        const componentId = s.component_id ? s.component_id : undefined;
        const daypartId = s.daypart_id ? s.daypart_id : undefined;

        const duplicate = [...daypartGlobalComponentMap.values()].find(
          (api) =>
            Number(api.component_id) === Number(componentId) &&
            Number(api.daypart_id) === Number(daypartId),
        );

        if (duplicate) {
          seenApiIds.add(Number(duplicate.id));

          return;
        }
        if (componentId != null && daypartId != null) {
          const daypart = daypartMap.get(daypartId);
          const narrative = narrativeGenerators[
            COLLECTIONS.DAYPART_GLOBAL_COMPONENTS
          ][USER_ACTIONS.UPDATE](
            eventName,
            daypart?.name ?? '',
            component.name,
          );

          ops.push({
            resource: resourceName,
            method: HttpMethod.POST,
            data: {
              component_id: componentId,
              daypart_id: daypartId,
              narrative,
              position: component.position,
            },
          });
        }
      });
    });
  });

  // Any API items not seen in UI traversal -> DELETE
  daypartGlobalComponents.forEach((apiItem) => {
    const id = Number(apiItem.id);
    if (!seenApiIds.has(id)) {
      const component = components.find(
        (comp) => comp.id === apiItem.component_id,
      );
      const daypart = daypartMap.get(apiItem.daypart_id);

      // Find event name by matching component type and variant
      const globalComponent = globalComponents.find((gc) => {
        const isTypeMatch =
          gc.componentType === component?.component_type?.name;
        if (!isTypeMatch) return false;

        if (gc.componentVariant && component?.config?.productsSource) {
          return (
            String(component.config.productsSource) ===
            String(gc.componentVariant)
          );
        }

        return true;
      });

      const narrative = narrativeGenerators[
        COLLECTIONS.DAYPART_GLOBAL_COMPONENTS
      ][USER_ACTIONS.DELETE](
        globalComponent?.label ?? '',
        daypart?.name ?? '',
        component?.name ?? '',
      );

      ops.push({
        resource: resourceName,
        method: HttpMethod.DELETE,
        id,
        data: { narrative },
      });
    }
  });

  return { data: ops };
};

export const validateTriggerConfigData = (rows: RowItem[]) => {
  const errors: CustomerTriggeredEventsErrors[] = [];

  rows.forEach((row) => {
    const usedDayparts = new Map<number, string>();

    row.components?.forEach((component) => {
      component.selectedDayparts?.forEach((daypart) => {
        if (usedDayparts.has(daypart.daypart_id)) {
          errors.push({
            rowId: row.id,
            daypartId: daypart.daypart_id,
            firstComponent: usedDayparts.get(daypart.daypart_id)!,
            duplicateComponent: component.id,
          });
        } else {
          usedDayparts.set(daypart.daypart_id, component.id);
        }
      });
    });
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};

export const areAllRowsValid = (
  rows: RowItem[],
  daypartsCount: number,
): boolean => {
  return rows.every((row) => {
    const allSelectedDaypartIds = new Set<number>();

    row.components?.forEach((component) => {
      component.selectedDayparts?.forEach((daypart) => {
        allSelectedDaypartIds.add(daypart.daypart_id);
      });
    });

    return allSelectedDaypartIds.size === daypartsCount;
  });
};

export const sortedDaypartsByStartTime = (dayparts: IDaypart[]) => {
  return [...dayparts].sort(
    (a, b) =>
      new Date(`1970-01-01T${a.default_start_time}`).getTime() -
      new Date(`1970-01-01T${b.default_start_time}`).getTime(),
  );
};

export const sortRowsWithCODOnTop = <T extends { name: string }>(
  rows: T[],
  codName: string,
): T[] => {
  return [...rows].sort((a, b) => {
    if (a.name === codName) return -1;
    if (b.name === codName) return 1;

    return a.name.localeCompare(b.name);
  });
};

export const handleScreenSequence = (
  next: ProjectConfigInterface['globalComponentsScreens'] = {},
  globalComponents: ScreenGlobalComponentInterface[],
  globalComponentsPath: string,
  updateComponent: {
    componentType: string;
    componentVariant: string;
    newScreenSequence: number;
    prevScreenSequence: number;
  },
  isSyncSequence: boolean = false,
) => {
  const componentMap: Record<string, number> = {};
  globalComponents.forEach((gc, idx) => {
    if (gc.componentVariant) componentMap[gc.componentVariant] = idx;
    else if (gc.componentType) componentMap[gc.componentType] = idx;
  });

  const reverseComponentMap: Record<string, string[]> = {
    [ComponentTypes.COD]: [ComponentTypes.COD_THANK_YOU, DyProductSource.DYR],
    [DyProductSource.DYS]: [ComponentTypes.COD_THANK_YOU, DyProductSource.DYR],
    [ComponentTypes.COD_THANK_YOU]: [ComponentTypes.COD, DyProductSource.DYS],
    [DyProductSource.DYR]: [ComponentTypes.COD, DyProductSource.DYS],
  };
  const syncComponentMap: Record<string, string[]> = {
    [ComponentTypes.COD]: [DyProductSource.DYS],
    [DyProductSource.DYS]: [ComponentTypes.COD],
    [ComponentTypes.COD_THANK_YOU]: [DyProductSource.DYR],
    [DyProductSource.DYR]: [ComponentTypes.COD_THANK_YOU],
  };

  const reverseComponents =
    reverseComponentMap[
      updateComponent.componentVariant || updateComponent.componentType
    ];
  const syncComponents =
    syncComponentMap[
      updateComponent.componentVariant || updateComponent.componentType
    ];

  if (!reverseComponents) return;

  reverseComponents.forEach((key) => {
    const idx = componentMap[key];
    const targetComponent = globalComponents[idx];
    if (
      targetComponent &&
      targetComponent.screenSequence !== updateComponent.prevScreenSequence
    ) {
      set(
        next,
        `${globalComponentsPath}[${idx}].screenSequence`,
        updateComponent.prevScreenSequence,
      );
    }
  });

  // paired items should trigger handleGlobalComponentsScreensChange to update screen sequence
  // instead of sync screen sequence here
  if (!syncComponents || !isSyncSequence) return;

  syncComponents.forEach((key) => {
    const idx = componentMap[key];
    const targetComponent = globalComponents[idx];
    if (
      targetComponent &&
      targetComponent.screenSequence !== updateComponent.newScreenSequence
    ) {
      set(
        next,
        `${globalComponentsPath}[${idx}].screenSequence`,
        updateComponent.newScreenSequence,
      );
    }
  });
};
